import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { DataTable } from "@/modules/shell/data-table";
import { EmptyState } from "@/modules/shell/EmptyState";
import { DataTableChrome } from "@/modules/shell/DataTableChrome";
import { DeskListHeader } from "@/modules/shell/DeskListHeader";
import { DeskSubNav } from "@/modules/shell/DeskSubNav";
import { posModuleTabs } from "@/modules/shell/desk-module-tabs";
import { PageFrame } from "@/modules/shell/PageFrame";
import { WarehouseSelect } from "@/modules/inventory/components/WarehouseSelect";
import {
  createPosTerminal,
  deactivatePosTerminal,
  listPosTerminals,
  reactivatePosTerminal,
  unbindPosTerminal,
  updatePosTerminal,
  type PosTerminal,
} from "@/shared/api/pos";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { terminalColumns } from "./terminals-columns";

export function TerminalsPage() {
  const { companySlug } = useParams();
  const confirmUi = useConfirm();
  const queryClient = useQueryClient();
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const terminalsQuery = useQuery({
    queryKey: queryKeys.pos.terminals(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () =>
      listPosTerminals(companySlug!, { includeDeactivated: true }),
  });
  const errorMsg = queryErrorMessage(
    terminalsQuery.error,
    terminalsQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  async function reload() {
    if (!companySlug) return;
    await queryClient.invalidateQueries({
      queryKey: queryKeys.pos.terminals(companySlug),
    });
  }

  const create = useMutation({
    mutationFn: async () => {
      const terminals = terminalsQuery.data?.terminals ?? [];
      const active = terminals.filter((t) => t.deactivatedAt == null);
      const n = active.length + 1;
      return createPosTerminal(companySlug!, {
        code: `t${n}`,
        name: `Terminal ${n}`,
        ...(warehouseId ? { warehouseId } : {}),
      });
    },
    onSuccess: async () => {
      setError(null);
      setStatus(null);
      await reload();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed");
    },
  });

  const rename = useMutation({
    mutationFn: async () => {
      if (!companySlug || !editingId) throw new Error("Nothing to save");
      const name = editName.trim();
      if (!name) throw new Error("Name required");
      return updatePosTerminal(companySlug, editingId, { name });
    },
    onSuccess: async (data) => {
      setError(null);
      setStatus(`Renamed to ${data.terminal.name}`);
      setEditingId(null);
      setEditName("");
      await reload();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed");
    },
  });

  const showBranchPicker = Boolean(terminalsQuery.data?.showBranchPicker);
  const adding = busy || create.isPending;
  const savingName = rename.isPending;

  function startEdit(t: { id: string; name: string }) {
    setEditingId(t.id);
    setEditName(t.name);
    setError(null);
    setStatus(null);
  }

  const columns = useMemo(
    () =>
      companySlug
        ? terminalColumns({
            companySlug,
            busy,
            savingName,
            showBranchPicker,
            editingId,
            editName,
            setEditName,
            onStartEdit: startEdit,
            onCancelEdit: () => {
              setEditingId(null);
              setEditName("");
            },
            onSaveRename: () => rename.mutate(),
            onUnbind: (t: PosTerminal) => {
              void (async () => {
                const ok = await confirmUi({
                  title: `Unbind ${t.name}?`,
                  description:
                    "Another device can then Open POS and bind to this terminal.",
                  confirmLabel: "Unbind",
                  destructive: true,
                });
                if (!ok) return;
                setBusy(true);
                setError(null);
                setStatus(null);
                try {
                  await unbindPosTerminal(companySlug, t.id);
                  setStatus(`${t.name} unbound`);
                  await reload();
                } catch (err: unknown) {
                  setError(
                    err instanceof Error ? err.message : "Failed",
                  );
                } finally {
                  setBusy(false);
                }
              })();
            },
            onDeactivate: (t: PosTerminal) => {
              void (async () => {
                const ok = await confirmUi({
                  title: `Deactivate ${t.name}?`,
                  description: "Close the drawer first if it is open.",
                  confirmLabel: "Deactivate",
                  destructive: true,
                });
                if (!ok) return;
                setBusy(true);
                setError(null);
                setStatus(null);
                try {
                  await deactivatePosTerminal(companySlug, t.id);
                  setStatus(`${t.name} deactivated`);
                  await reload();
                } catch (err: unknown) {
                  setError(
                    err instanceof Error ? err.message : "Failed",
                  );
                } finally {
                  setBusy(false);
                }
              })();
            },
            onReactivate: (t: PosTerminal) => {
              setBusy(true);
              setError(null);
              setStatus(null);
              void reactivatePosTerminal(companySlug, t.id)
                .then(async () => {
                  setStatus(`${t.name} reactivated`);
                  await reload();
                })
                .catch((err: unknown) =>
                  setError(
                    err instanceof Error ? err.message : "Failed",
                  ),
                )
                .finally(() => setBusy(false));
            },
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- match CustomersPage pattern
    [
      companySlug,
      busy,
      savingName,
      showBranchPicker,
      editingId,
      editName,
      confirmUi,
    ],
  );

  if (!companySlug) return null;

  const terminals = terminalsQuery.data?.terminals ?? [];
  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  return (
    <PageFrame>
      <DataTableChrome>
      <DeskListHeader
        title="POS terminals"
        description="Open a register here. Cashiers use user code + PIN — set those under People, not buried only in Settings."
        tabs={
          <DeskSubNav
            placement="header"
            items={posModuleTabs(companySlug)}
            aria-label="POS views"
          />
        }
      />

      <div className="mt-4">
        <WarehouseSelect
          companySlug={companySlug}
          value={warehouseId}
          onChange={setWarehouseId}
          disabled={adding}
          label="New terminal warehouse"
        />
      </div>

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}
      {status && (
        <Alert variant="muted" className="mt-4">
          {status}
        </Alert>
      )}

      <ol className="mt-6 m-0 list-decimal space-y-1.5 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]/70 px-5 py-4 pl-9 text-sm leading-relaxed text-[var(--color-muted)]">
        <li>Sign in with Google (owner/manager).</li>
        <li>Open a terminal below (binds this browser as the register device).</li>
        <li>
          Cashier enters their user code + PIN (
          <Link
            to={`/c/${companySlug}/settings/org#people`}
            className="text-[var(--color-foreground)] underline-offset-2 hover:underline"
          >
            People & PINs
          </Link>
          ).
        </li>
        <li>Open the drawer, then sell.</li>
        <li>
          Exit POS → PIN login; Logout on that screen leaves POS mode (Google).
        </li>
        <li>Unbind here to move the register to another device.</li>
      </ol>

      {terminalsQuery.isPending && (
        <Alert variant="muted" className="mt-8">
          Loading terminals…
        </Alert>
      )}

      {!terminalsQuery.isPending && terminals.length === 0 && !displayError ? (
        <div className="mt-8">
          <EmptyState
            title="No terminals yet"
            description="Add one, or create a new company to seed Terminal 1."
            action={
              <Button
                type="button"
                size="sm"
                disabled={adding}
                onClick={() => create.mutate()}
              >
                {adding ? "Adding…" : "Add terminal"}
              </Button>
            }
          />
        </div>
      ) : null}
      {!terminalsQuery.isPending && terminals.length > 0 ? (
        <DataTable
          listId="pos-terminals"
          companySlug={companySlug}
          columns={columns}
          data={terminals}
          getRowId={(r) => r.id}
          manualSorting={false}
          onRefresh={() => void terminalsQuery.refetch()}
        />
      ) : null}

      <p className="mt-6">
        <Button variant="ghost" asChild>
          <Link to={`/c/${companySlug}`} className="no-underline">
            ← Company
          </Link>
        </Button>
      </p>
          </DataTableChrome>
    </PageFrame>
  );
}
